import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ArrowRight, Menu, X, Battery, Zap, Train, Forklift, FlaskConical, Shield, FileCheck, Cpu, Gauge, Car, FileText, ExternalLink, Handshake, Users2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  // Code of Conduct documents - opens in new tab
  const codeOfConductDocs = [
    { 
      name: 'Supplier Code of Conduct', 
      url: 'https://customer-assets.emergentagent.com/job_798250da-abbc-4d34-9db9-f11615701563/artifacts/2mfsq1gj_Gegadyne%20Energy_Supplier%20Code%20of%20Conduct.pdf',
      icon: Handshake 
    },
    { 
      name: 'Equal Opportunity Policy', 
      url: 'https://customer-assets.emergentagent.com/job_798250da-abbc-4d34-9db9-f11615701563/artifacts/19qjn39i_Gegadyne_Energy_Equal_Opportunity_Policy%2027th%20june%202023.pdf',
      icon: Users2 
    },
  ];

  const navigationItems = [
    { 
      name: 'Multi-Ion™ Technology', 
      href: '/multi-ion-technology',
      hasDropdown: true,
      dropdownItems: [
        { name: 'How Multi-Ion Works', href: '/multi-ion-technology/how-it-works', icon: FlaskConical },
        { name: 'Multi-Ion Animation', href: '/multi-ion-technology/animation', icon: Cpu },
        { name: 'Why Non-Lithium', href: '/multi-ion-technology/why-non-lithium', icon: Shield },
        { name: 'Performance Comparison', href: '/multi-ion-technology/comparison', icon: Gauge },
      ]
    },
    { 
      name: 'Products', 
      href: '/products',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Traction Battery', href: '/products/traction-battery', icon: Forklift },
        { name: 'Energy Storage', href: '/products/energy-storage', icon: Battery },
        { name: 'Rail Systems', href: '/products/rail-systems', icon: Train },
        { name: 'E-Mobility', href: '/e-mobility', icon: Car },
      ]
    },
    { 
      name: 'Safety', 
      href: '/safety',
      hasDropdown: false
    },
    { 
      name: 'Validation', 
      href: '/validation',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Validated Performance', href: '/validation/performance', icon: FileCheck },
      ]
    },
    { name: 'About', href: '/about', hasDropdown: false },
    { name: 'Media', href: '/media', hasDropdown: false },
    { name: 'Careers', href: '/careers', hasDropdown: false },
    { 
      name: 'Code of Conduct', 
      href: '#',
      hasDropdown: true,
      isExternalLinks: true,
      dropdownItems: codeOfConductDocs
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleMouseEnter = (itemName) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(itemName);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleDropdownClick = (e, href) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
    navigate(href);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gray-950/95 backdrop-blur-md border-b border-white/10 py-3' 
          : 'bg-transparent py-5'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo"
          >
            <img 
              src="/images/gegadyne-logo.png" 
              alt="Gegadyne Energy" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigationItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                {item.isExternalLinks ? (
                  <button
                    className={`flex items-center gap-1.5 px-4 py-2 text-gray-300 hover:text-white text-sm font-medium transition-colors rounded-lg hover:bg-white/5 ${
                      activeDropdown === item.name ? 'text-white bg-white/5' : ''
                    }`}
                  >
                    {item.name}
                    {item.hasDropdown && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`} />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1.5 px-4 py-2 text-gray-300 hover:text-white text-sm font-medium transition-colors rounded-lg hover:bg-white/5 ${
                      activeDropdown === item.name ? 'text-white bg-white/5' : ''
                    }`}
                  >
                    {item.name}
                    {item.hasDropdown && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`} />
                    )}
                  </Link>
                )}

                {/* Dropdown Menu */}
                {item.hasDropdown && activeDropdown === item.name && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-64 bg-gray-900/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                    onMouseEnter={() => handleMouseEnter(item.name)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="p-2">
                      {item.dropdownItems.map((dropdownItem) => {
                        const Icon = dropdownItem.icon;
                        
                        // Handle external links (PDFs) - open in new tab
                        if (item.isExternalLinks || dropdownItem.url) {
                          return (
                            <a
                              key={dropdownItem.name}
                              href={dropdownItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setActiveDropdown(null)}
                              className="group flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                                <Icon className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-white text-sm font-medium group-hover:text-emerald-400 transition-colors flex items-center gap-2">
                                  {dropdownItem.name}
                                  <ExternalLink className="w-3 h-3 text-gray-500" />
                                </p>
                              </div>
                            </a>
                          );
                        }
                        
                        // Regular internal links
                        return (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            onClick={(e) => handleDropdownClick(e, dropdownItem.href)}
                            className="group flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                              <Icon className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-white text-sm font-medium group-hover:text-emerald-400 transition-colors">
                                {dropdownItem.name}
                              </p>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contact"
              className="group flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300"
              data-testid="header-cta"
            >
              Contact Us
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-400 hover:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-gray-950/98 backdrop-blur-lg border-b border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="max-w-7xl mx-auto px-4 py-6">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                <div key={item.name}>
                  {item.isExternalLinks ? (
                    <div className="flex items-center justify-between px-4 py-3 text-gray-300 text-base font-medium">
                      {item.name}
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => !item.hasDropdown && setIsMobileMenuOpen(false)}
                      className="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg text-base font-medium transition-colors"
                    >
                      {item.name}
                      {item.hasDropdown && <ChevronDown className="w-5 h-5" />}
                    </Link>
                  )}
                  
                  {item.hasDropdown && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.dropdownItems.map((dropdownItem) => {
                        const Icon = dropdownItem.icon;
                        
                        // Handle external links (PDFs) in mobile menu
                        if (item.isExternalLinks || dropdownItem.url) {
                          return (
                            <a
                              key={dropdownItem.name}
                              href={dropdownItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                            >
                              <Icon className="w-4 h-4 text-emerald-400" />
                              <span className="text-sm flex items-center gap-2">
                                {dropdownItem.name}
                                <ExternalLink className="w-3 h-3 text-gray-500" />
                              </span>
                            </a>
                          );
                        }
                        
                        return (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                          >
                            <Icon className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm">{dropdownItem.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Mobile CTA */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white w-full px-6 py-3 rounded-lg text-base font-semibold transition-all"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
